import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";

import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const { user, checked } = useAuth();
  const [restaurant, setRestaurant] = useState(null);

  const [branches, setBranches] = useState([]);

  const [branchId, setBranchId] = useState(
    localStorage.getItem("branchId") || "all"
  );

  const [online, setOnline] = useState(
    typeof navigator !== "undefined"
      ? navigator.onLine
      : true
  );

  const [wsConnected, setWsConnected] = useState(false);

  const listeners = useRef({});

  const wsRef = useRef(null);

const refreshRestaurantData = useCallback(async () => {
  try {
    const [restaurantResponse, branchesResponse] =
      await Promise.all([
        api.restaurants(),
        api.branches(),
      ]);

    setRestaurant(
      restaurantResponse.data?.[0] || null
    );

    setBranches(
      Array.isArray(branchesResponse.data)
        ? branchesResponse.data
        : []
    );
  } catch (error) {
    console.error(
      "Failed to refresh restaurant data:",
      error
    );
    throw error;
  }
}, []);
  // ---------------------------------------------------------
  // Load restaurant and branches
  // ---------------------------------------------------------

  useEffect(() => {
  if (!checked || !user) return;

  api.restaurants()
    .then((r) => {
      setRestaurant(r.data?.[0] || null);
    })
    .catch(() => {});

  api.branches()
    .then((r) => {
      const branchList = Array.isArray(r.data)
        ? r.data
        : [];

      setBranches(branchList);

      const savedBranchId = localStorage.getItem("branchId");

      if (
        savedBranchId &&
        branchList.some((branch) => branch.id === savedBranchId)
      ) {
        setBranchId(savedBranchId);
      } else if (branchList[0]) {
        setBranchId(branchList[0].id);

        localStorage.setItem(
          "branchId",
          branchList[0].id
        );
      } else {
        setBranchId("all");
        localStorage.removeItem("branchId");
      }
    })
    .catch(() => {});
}, [checked, user]);


  // ---------------------------------------------------------
  // Browser online / offline status
  // ---------------------------------------------------------

  useEffect(() => {
    const on = () => setOnline(true);

    const off = () => setOnline(false);

    window.addEventListener("online", on);
    window.addEventListener("offline", off);

    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);


  // ---------------------------------------------------------
  // WebSocket with auto reconnect
  // ---------------------------------------------------------

  useEffect(() => {
    let stop = false;
    let timer = null;


    const connect = () => {
      if (stop) return;

      try {
        const ws = new WebSocket(wsUrl());

        wsRef.current = ws;


        ws.onopen = () => {
          if (stop) return;

          setWsConnected(true);
        };


        ws.onclose = () => {
          setWsConnected(false);

          if (!stop) {
            timer = setTimeout(
              connect,
              3000
            );
          }
        };


        ws.onerror = () => {
          try {
            ws.close();
          } catch (e) {}
        };


        ws.onmessage = (e) => {
          try {
            const {
              event,
              payload,
            } = JSON.parse(e.data);


            (
              listeners.current[event] || []
            ).forEach((cb) => {
              try {
                cb(payload);
              } catch (err) {}
            });


            (
              listeners.current["*"] || []
            ).forEach((cb) => {
              try {
                cb(event, payload);
              } catch (err) {}
            });

          } catch (err) {
            // Ignore invalid websocket messages
          }
        };

      } catch (err) {
        setWsConnected(false);

        if (!stop) {
          timer = setTimeout(
            connect,
            3000
          );
        }
      }
    };


    connect();


    return () => {
      stop = true;

      if (timer) {
        clearTimeout(timer);
      }

      try {
        wsRef.current?.close();
      } catch (e) {}

      wsRef.current = null;
    };

  }, []);


  // ---------------------------------------------------------
  // Subscribe to realtime events
  // ---------------------------------------------------------

  const subscribe = useCallback(
    (event, cb) => {
      if (!listeners.current[event]) {
        listeners.current[event] = [];
      }


      listeners.current[event].push(cb);


      return () => {
        listeners.current[event] =
          (
            listeners.current[event] || []
          ).filter(
            (x) => x !== cb
          );
      };
    },
    []
  );


  // ---------------------------------------------------------
  // Branch change
  // ---------------------------------------------------------

  const changeBranch = (id) => {
    setBranchId(id);

    localStorage.setItem(
      "branchId",
      id
    );
  };


  // ---------------------------------------------------------
  // Sync status
  //
  // IMPORTANT:
  // WebSocket disconnected does NOT mean syncing.
  // Actual syncing is not implemented here yet.
  // ---------------------------------------------------------

  const syncStatus = !online
    ? "offline"
    : "online";


  return (
    <AppContext.Provider
      value={{
        restaurant,
        branches,
        branchId,
        changeBranch,
        subscribe,
        refreshRestaurantData,
        online,
        wsConnected,

        syncStatus,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};


export const useApp = () =>
  useContext(AppContext);